import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OnboardingExit } from '../components/auth-shells.js';
import { BrowserWindow } from '../components/browser-window.js';
import { Mag } from '../components/mag.js';
import {
  type ScrapeBrainSnapshot,
  ScrapeInspector,
  useScrapeBrain,
} from '../components/scrape-live-preview.js';
import {
  type ExtractionStepGroup,
  type ExtractionStepRow,
  fetchExtractionProgress,
  scrapeUrlIntoBrief,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { brandsQuery, meQuery } from '../lib/queries.js';

// §12.2L — the live "mag is reading" surface. The founder lands here
// the instant the scrape endpoint returns. We poll every 1.5s and
// render the 22 underlying steps as they transition running → done.
//
// Two view modes, sessionStorage-persisted toggle in the footer:
//
//  - friendly mode (default) — mag centerstage with aura+breathe,
//    a cycling thought line that rotates mag-voice phrases every
//    ~3s ("scanning your headlines…" → "tasting your palette…"),
//    and 4 phase tiles (one per group: reading you / seeing your
//    style / going deeper / filing it). each tile shows a dot row
//    instead of per-step bullets; sage tint + outcome receipt on
//    completion. small + scannable — the founder sees mag working
//    without reading a system log.
//
//  - detail mode — the prior verbose surface. 4 CollapsibleStep-
//    Group cards with per-step bullets, .mag-row-shimmer on the
//    active row, .mag-check-pop on completion, etc. behind a
//    "what mag's actually doing" footer disclosure for power-
//    users + ops debugging stalls.
//
// Receipt stream stays the same shape in both modes but shows last
// 2 by default with a "↘ N more" disclosure; tucked below the tiles
// so it doesn't dominate the page.

interface ScrapeSearch {
  runId?: string;
}

export const Route = createFileRoute('/brand/$slug/onboarding/scrape')({
  validateSearch: (search: Record<string, unknown>): ScrapeSearch => {
    if (typeof search['runId'] === 'string') {
      return { runId: search['runId'] };
    }
    return {};
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { brand };
  },
  component: ScrapeLivePage,
});

/** Mag-voice cycling phrases, per group. Rotated by the
 *  useCyclingPhrase hook every CYCLING_PHRASE_MS while that group is
 *  active. Hidden when no group is active (warm-up / between groups).
 *  More playful than the underlying step names — "tasting your
 *  palette" beats "visual_read_guidelines" for the founder. */
const CYCLING_PHRASES: Record<ExtractionStepGroup, readonly MessageDescriptor[]> = {
  fetch: [
    msg`scanning your headlines…`,
    msg`tasting your hero copy…`,
    msg`logging the obvious bits…`,
    msg`noting how you talk…`,
  ],
  visual: [
    msg`tasting your palette…`,
    msg`eyeing your typefaces…`,
    msg`screenshotting your hero…`,
    msg`stashing your logo…`,
    msg`pinning your favicon down…`,
  ],
  crawl: [
    msg`sneaking onto your other pages…`,
    msg`peeking at your subnav…`,
    msg`reading your fine print…`,
    msg`cataloguing every visit…`,
  ],
  extract: [
    msg`pinning your products down…`,
    msg`introducing myself to your team…`,
    msg`collecting your customer quotes…`,
    msg`writing it all down…`,
    msg`tidying up the bits…`,
  ],
};
// [12.2L-173] Steps the orchestrator runs AFTER returning control
// to the founder. The terminal calc excludes these from the must-
// complete set so the founder can navigate to /brief while the
// long-tail work finishes in the background. Kept in lockstep with
// apps/api/src/brand-brain-scrape-orchestrator.ts DEFERRED_STEP_IDS
// — adding another deferred step requires editing both.
const DEFERRED_STEP_IDS = new Set<ExtractionStepRow['step']>([
  'visual_classify_landing_images',
  'crawl_classify_subpage_images',
  'extract_motifs',
]);

const CYCLING_PHRASE_MS = 3_000;
const WARMUP_PHRASE = msg`getting ready…`;
const TERMINAL_PHRASE = msg`got it. let's go.`;

const STEP_LABELS: Record<ExtractionStepRow['step'], MessageDescriptor> = {
  fetch_html: msg`reading your html`,
  extract_identity: msg`sketching your identity`,
  extract_voice: msg`finding your voice`,
  extract_customer: msg`reading your customer`,
  write_brain: msg`writing your brain`,
  persist_landing_page: msg`keeping the landing copy verbatim`,
  enqueue_landing_thumbnail: msg`queuing a thumbnail`,
  stamp_marketing_url: msg`noting your url`,
  stamp_target_market: msg`noting your target market`,
  visual_screenshot: msg`taking a screenshot`,
  visual_read_guidelines: msg`reading your colors + type`,
  visual_store_logo: msg`storing your logo`,
  visual_store_favicon: msg`storing your favicon`,
  visual_upsert_guidelines: msg`filing your visual system`,
  visual_classify_landing_images: msg`classifying landing images`,
  crawl_subpages: msg`crawling your sub-pages`,
  crawl_persist_pages: msg`reading each sub-page`,
  crawl_classify_subpage_images: msg`classifying sub-page images`,
  extract_products: msg`extracting your products`,
  extract_team: msg`extracting your team`,
  extract_testimonials: msg`extracting testimonials`,
  extract_voice_examples: msg`pulling voice examples`,
  extract_design_system: msg`reading your design system`,
  extract_motifs: msg`picking your motifs`,
};

function ScrapeLivePage() {
  const { brand: brandSnapshot } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The route context brand is a beforeLoad SNAPSHOT — non-reactive. In
  // the URL-first create flow brand.marketingUrl is stamped server-side
  // moments before we land here, so if the snapshot was taken before the
  // brands cache caught up, marketingUrl is null and the address bar
  // shows the https://<slug> fallback ("https://commitmind") forever.
  // Read the brand LIVE from the brands cache and force a refetch on
  // mount so the address bar resolves to the real site the moment
  // marketingUrl lands (create-form refresh, the orchestrator's
  // stamp_marketing_url step, or this refetch — whichever wins).
  const { data: liveBrands } = useQuery({ ...brandsQuery(), refetchOnMount: 'always' });
  const brand = liveBrands?.find((b) => b.id === brandSnapshot.id) ?? brandSnapshot;
  useBrandTitle(brand.slug, ['mag is reading']);

  // Poll every 1.5s until terminal (all steps are done/failed/skipped).
  const { data } = useQuery({
    queryKey: ['extraction-progress', brand.id],
    queryFn: () => fetchExtractionProgress(brand.id),
    refetchInterval: (q) => {
      const steps = q.state.data?.steps ?? [];
      if (steps.length === 0) return 1500;
      const stillWorking = steps.some((s) => s.status === 'pending' || s.status === 'running');
      return stillWorking ? 1500 : false;
    },
    refetchIntervalInBackground: false,
  });

  const steps = data?.steps ?? [];

  // [12.2L-173] Foreground terminal: the founder is free to move on
  // the moment every NON-deferred step is done. Image classification
  // (visual_classify_landing_images, crawl_classify_subpage_images)
  // is the long tail — runs ~1-2min and only needs to land in the
  // brain for future creative work, not for the founder to write
  // their brief. So we treat it as background work and unblock
  // navigation. Counts are recomputed on non-deferred steps; the
  // page surfaces a small "still cooking" line for the deferred
  // ones while the CTA is live.
  const nonDeferredSteps = useMemo(
    () => steps.filter((s) => !DEFERRED_STEP_IDS.has(s.step)),
    [steps],
  );
  const deferredSteps = useMemo(() => steps.filter((s) => DEFERRED_STEP_IDS.has(s.step)), [steps]);
  const terminal =
    nonDeferredSteps.length > 0 &&
    nonDeferredSteps.every(
      (s) => s.status === 'done' || s.status === 'failed' || s.status === 'skipped',
    );
  const deferredInFlight =
    deferredSteps.length > 0 &&
    deferredSteps.some((s) => s.status === 'pending' || s.status === 'running');

  // Hard scrape failure. When fetch_html fails (site private / not
  // published / unreachable / blocking us) the orchestrator marks every
  // downstream step 'skipped' and bails. Without special-casing that,
  // the run reads as "terminal" and the page shows the cheerful
  // "here's what i found" summary over an EMPTY brain — or, in the brief
  // window before the 20 skip-writes land, spins forever (no running
  // step ⇒ not stalled; not all-terminal ⇒ not done). Detect it off the
  // fetch step so we can show an error surface the moment it fails,
  // ahead of the skip cascade.
  const fetchStep = useMemo(() => steps.find((s) => s.step === 'fetch_html') ?? null, [steps]);
  const scrapeFailed = fetchStep?.status === 'failed';
  // withProgressStep stores the failure as "something went sideways on
  // this step — <message>"; strip that prefix so the founder sees the
  // bare reason ("mag could not read the site").
  const failureDetail =
    fetchStep?.receipt?.replace(/^something went sideways on this step — /, '').trim() || null;
  // Out-of-tokens reads differently from a fetch failure: retrying or
  // changing the url won't help — the founder needs to top up. Detect it
  // off the (now un-masked, see the orchestrator) failure message so the
  // banner can swap to a top-up CTA instead of a retry.
  const outOfTokens = !!failureDetail && /out of mag tokens/i.test(failureDetail);

  // [12.2L-47] When the orchestrator finishes, force-invalidate the
  // brand-brain query so the next page the founder navigates to
  // (/brief, /brief/confirm, /brand/$slug/team, etc.) reads fresh
  // data instead of the pre-onboarding empty cache. without this,
  // the brand-brain query's 60s staleTime can serve the
  // not-yet-extracted state to the team / products / testimonials
  // hubs and they show empty until the founder manually hits
  // "re-extract from site" — even though the orchestrator finished
  // saving the data minutes ago. invalidate fires once per
  // terminal-transition via a ref guard so it doesn't loop on
  // subsequent re-renders of the terminal page.
  const invalidatedRef = useRef(false);
  useEffect(() => {
    if (terminal && !invalidatedRef.current) {
      invalidatedRef.current = true;
      void queryClient.invalidateQueries({ queryKey: ['brand-brain', brand.id] });
    }
  }, [terminal, queryClient, brand.id]);

  // Stall detection — a running step that hasn't completed in
  // STALL_THRESHOLD_MS is treated as stuck. Without this the page
  // poll forever on a hung orchestrator (orphan playwright session,
  // unbounded LLM call, etc.). Founder sees the "looks like i'm
  // stuck" card + can restart.
  //
  // [12.2L-173] Bumped from 90s → 5min. Recent orchestrator runs are
  // taking ~4min wall-clock (vision-pass alone is ~30s, voice-examples
  // extractor can take another ~30s+, plus crawl + subpage classify).
  // At 90s the page was crying stall during steps that were
  // legitimately doing work — a fresh founder would smash "start over"
  // on a perfectly healthy run. 5min is still well below any plausible
  // hang signal while covering the new long-tail steps.
  const STALL_THRESHOLD_MS = 300_000;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (terminal) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, [terminal]);
  const stalled = useMemo(() => {
    if (terminal || steps.length === 0) return false;
    const running = steps.filter((s) => s.status === 'running' && s.startedAt !== null);
    if (running.length === 0) return false;
    // Use the most-recently-started running step — if anything has
    // moved in the last 90s, we're not stuck.
    const newestStart = running.reduce((max, s) => {
      const t = s.startedAt ? new Date(s.startedAt).getTime() : 0;
      return t > max ? t : max;
    }, 0);
    return newestStart > 0 && nowMs - newestStart > STALL_THRESHOLD_MS;
  }, [steps, terminal, nowMs]);

  const restartMutation = useMutation({
    mutationFn: async () => {
      if (!brand.marketingUrl) {
        throw new Error("i don't have a marketing url for this brand to re-scrape.");
      }
      return scrapeUrlIntoBrief(brand.marketingUrl, brand.id);
    },
    onSuccess: () => {
      // The polling reads by brand id + picks up the newest run.
      // Resetting the query forces the next poll to start from a
      // clean slate instead of carrying stale step rows from the
      // stalled run.
      void queryClient.resetQueries({ queryKey: ['extraction-progress', brand.id] });
      setNowMs(Date.now());
    },
  });

  // The currently-running step label — drives the "thought" line
  // shown under mag while she's working. Deferred steps are
  // explicitly excluded so the ticker doesn't keep reading
  // "classifying sub-page images" after the foreground completes —
  // those steps are background work, surfaced via the "still
  // classifying your images" hint instead.
  const activeStep = useMemo(() => {
    const running = steps.filter((s) => s.status === 'running' && !DEFERRED_STEP_IDS.has(s.step));
    if (running.length === 0) return null;
    return running.sort((a, b) => b.orderIdx - a.orderIdx)[0] ?? null;
  }, [steps]);

  // [12.2L-173] Next pending step — drives the "↗ next:" line in the
  // inspector header. Same deferred-exclusion as activeStep.
  const nextPendingStep = useMemo(() => {
    const pending = steps.filter((s) => s.status === 'pending' && !DEFERRED_STEP_IDS.has(s.step));
    if (pending.length === 0) return null;
    return pending.sort((a, b) => a.orderIdx - b.orderIdx)[0] ?? null;
  }, [steps]);

  // The active group drives the cycling-phrase rotation. Reads from
  // the (foreground-only) active step so the phrase pool matches what
  // Mag is actually working on.
  const activeGroupKind = activeStep?.groupKind ?? null;

  // Cycling thought phrase under mag. Rotates every CYCLING_PHRASE_MS
  // through the active group's phrase pool. Pauses while terminal /
  // stalled / no active group.
  const cyclingPhrase = useCyclingPhrase({
    activeGroup: activeGroupKind,
    terminal,
    stalled,
  });

  // [12.2L-173] Brain snapshot drives the inspector panel inside the
  // browser window. Reuses the same brand-brain query the inspector
  // reads from — react-query dedupes by key so no double-fetch.
  const { brain, screenshots: brainScreenshots, hasAnyLanded } = useScrapeBrain(brand.id);

  // Sanitize what we type into the address bar — strip the trailing
  // slash so it reads as a real domain ("gestionalo.uy") rather than
  // "gestionalo.uy/". Default to the brand slug if no marketingUrl.
  const addressBarUrl = useMemo(() => {
    const raw = brand.marketingUrl ?? `https://${brand.slug}`;
    return raw.replace(/\/$/, '');
  }, [brand.marketingUrl, brand.slug]);

  // The activity-strip line at the bottom of the viewport. Reads the
  // currently-running step's label so the founder always knows what
  // Mag's doing right now ("peeking at /para-profesionales", etc.).
  const currentActivity = useMemo(() => {
    if (terminal) return null;
    if (stalled) return null;
    if (activeStep) return i18n._(STEP_LABELS[activeStep.step]);
    return i18n._(msg`opening the page…`);
  }, [terminal, stalled, activeStep]);

  // Progress percentage — drives the loading bar at the top of the
  // address bar. Cap at 99% while non-terminal so it doesn't claim
  // "done" before the orchestrator commits.
  const progressFraction = useMemo(() => {
    if (steps.length === 0) return 0.04;
    if (terminal) return 1;
    const done = steps.filter((s) => s.status === 'done').length;
    return Math.min(0.99, done / steps.length);
  }, [steps, terminal]);

  // Hard-failure surface takes precedence over the working/terminal
  // views: a fetch_html failure means there's nothing to "summarize",
  // so we never want the cheerful terminal banner or the spinner here.
  if (scrapeFailed) {
    return (
      <main className="relative min-h-dvh bg-[var(--cream)]">
        <OnboardingExit brandSlug={brand.slug} />
        <section className="mx-auto flex w-full max-w-[640px] flex-col gap-6 px-4 py-16 sm:px-6">
          <ScrapeErrorBanner
            brandName={brand.name}
            detail={failureDetail}
            outOfTokens={outOfTokens}
            // The picker, NOT /settings/billing. This founder has no
            // subscription yet — they are mid-onboarding and the brand
            // routes are gated — so billing settings is the wrong room:
            // it is where an existing subscriber tops up. /onboarding/plan
            // is purpose-built, carries the brand through checkout and
            // returns them to /brand/<slug>/home afterwards.
            onTopUp={() =>
              void navigate({
                to: '/onboarding/plan',
                search: { brand: brand.slug, reason: 'out_of_tokens' },
              })
            }
            onRetry={() => restartMutation.mutate()}
            retrying={restartMutation.isPending}
            retryError={
              restartMutation.error instanceof Error ? restartMutation.error.message : null
            }
            onChangeUrl={() =>
              void navigate({
                to: '/brand/$slug/onboarding/url',
                params: { slug: brand.slug },
              })
            }
            onFillManually={() =>
              void navigate({
                to: '/brand/$slug/brief',
                params: { slug: brand.slug },
                search: { onboarding: '1' as const },
              })
            }
          />
        </section>
      </main>
    );
  }

  return (
    <main className="relative min-h-dvh bg-[var(--cream)]">
      {/* [12.2L-52] always-visible exit affordance. before this the
          scrape page had no escape — if the orchestrator stalled and
          the restart couldn't fire (e.g. brand.marketingUrl missing),
          the founder was trapped on a "this stalled" surface with no
          way back to the brand. fixed-position via OnboardingExit so
          it sits in the corner above mag/the tiles regardless of
          scroll. /brand/$slug/summary lands them on the brand-hub
          summary card where the partial brain rows + a "scan again"
          option live. */}
      <OnboardingExit brandSlug={brand.slug} />
      <section className="mx-auto flex w-full max-w-[1080px] flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
        {/* Two header modes:
              - working: thin status strip (Mag + small heading)
              - terminal: a richer "i'm done — here's the inventory"
                summary banner with stat pills + Mag-voice bridge
                copy + prominent CTA into the brief review. */}
        {terminal ? (
          <TerminalSummaryBanner
            brain={brain}
            brandName={brand.name}
            deferredInFlight={deferredInFlight}
            onContinue={() => {
              void navigate({
                to: '/brand/$slug/brief',
                params: { slug: brand.slug },
                search: { onboarding: '1' as const },
              });
            }}
          />
        ) : (
          <header className="flex flex-wrap items-center gap-4">
            <div className="mag-stagger-in shrink-0" style={{ animationDelay: '60ms' }}>
              <Mag size={56} mood="casting" trackCursor />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="mag-stagger-in font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]"
                style={{ animationDelay: '120ms' }}
              >
                {'↘'} <Trans>mag is reading</Trans>
              </p>
              <h1
                className="mag-stagger-in mt-1 font-display text-[22px] sm:text-[28px] font-black leading-[1.05] tracking-[-0.03em] text-[var(--ink)]"
                style={{ animationDelay: '200ms' }}
              >
                <Trans>reading {brand.name.toLowerCase()}.</Trans>
              </h1>
            </div>
          </header>
        )}

        {stalled ? (
          <StallBanner
            stalledStep={activeStep ? i18n._(STEP_LABELS[activeStep.step]) : null}
            onRestart={() => restartMutation.mutate()}
            restarting={restartMutation.isPending}
            restartError={
              restartMutation.error instanceof Error ? restartMutation.error.message : null
            }
          />
        ) : null}

        {/* Browser + inspector sidecar. Browser starts at flex-1
            (claims the whole 1080px container). The moment any
            artifact lands (hasAnyLanded), the inspector aside
            transitions from w-0/opacity-0 to its natural width with
            a slide+fade — the browser shrinks to make room, which
            reads as "Mag translates the browser left and pins the
            findings panel on the right." Skeleton inside the
            browser keeps the viewport visually loaded from frame 1
            so the founder never sees an empty white box. */}
        <div className="flex flex-col gap-5 md:flex-row md:items-start">
          <div className="min-w-0 flex-1">
            <BrowserWindow
              url={addressBarUrl}
              progress={progressFraction}
              done={terminal}
              screenshots={brainScreenshots}
              cyclingPhrase={cyclingPhrase}
              currentActivity={currentActivity}
            />
          </div>

          {/* Sidecar inspector — slides in from the right with a
              width + opacity transition the moment any brain
              artifact lands. Outer aside is overflow-hidden so the
              slide reveals cleanly; the extra pr-2/pb-2 padding
              gives the inner card's hard plum shadow room to render
              without bleeding past the clip and showing up as a
              stray line at the bottom-right corner. */}
          <aside
            aria-hidden={!hasAnyLanded && activeStep === null}
            className={`overflow-hidden transition-all duration-[700ms] ease-[cubic-bezier(0.2,0.7,0.3,1)] ${
              hasAnyLanded || activeStep !== null
                ? 'opacity-100 md:w-[376px]'
                : 'pointer-events-none w-0 opacity-0 md:w-0'
            }`}
          >
            <div className="md:w-[376px] pb-2 pr-2">
              <div className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--plum)]">
                <div className="flex flex-col gap-1">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                    {'↘'} <Trans>what i'm finding</Trans>
                  </p>
                  {/* Now/next ticker so the founder always sees what
                      Mag is working on AND what's queued up next.
                      Updates as the orchestrator moves through
                      steps; falls quiet at terminal. */}
                  {!terminal ? (
                    <NowNextTicker activeStep={activeStep} nextStep={nextPendingStep} />
                  ) : null}
                </div>
                <ScrapeInspector brain={brain} />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

/** Hook — rotates through mag-voice cycling phrases for the currently
 *  active group every CYCLING_PHRASE_MS. When the active group changes,
 *  resets to the first phrase of the new group's pool. terminal / no
 *  active group → fixed phrases (TERMINAL_PHRASE / WARMUP_PHRASE).
 *  stalled → silenced so the stall banner reads instead. */
function useCyclingPhrase({
  activeGroup,
  terminal,
  stalled,
}: {
  activeGroup: ExtractionStepGroup | null;
  terminal: boolean;
  stalled: boolean;
}): string {
  const [index, setIndex] = useState(0);
  // Reset to phrase 0 whenever the active group changes — feels like
  // mag "starting" the new task instead of mid-flow. React's
  // "store previous prop in state" pattern (avoids an effect that
  // biome treats as redundant).
  const [prevGroup, setPrevGroup] = useState<ExtractionStepGroup | null>(activeGroup);
  if (prevGroup !== activeGroup) {
    setPrevGroup(activeGroup);
    setIndex(0);
  }
  useEffect(() => {
    if (terminal || stalled || !activeGroup) return;
    const pool = CYCLING_PHRASES[activeGroup];
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % pool.length);
    }, CYCLING_PHRASE_MS);
    return () => window.clearInterval(id);
  }, [activeGroup, terminal, stalled]);
  if (terminal) return i18n._(TERMINAL_PHRASE);
  if (stalled) return '';
  if (!activeGroup) return i18n._(WARMUP_PHRASE);
  const pool = CYCLING_PHRASES[activeGroup];
  const phrase = pool[index % pool.length] ?? WARMUP_PHRASE;
  return i18n._(phrase);
}

/** Terminal completion banner — replaces the lean working header
 *  once every foreground step finishes. Sets up the brief-review
 *  hand-off with a Mag-voice bridge, a glanceable inventory of what
 *  Mag pulled, and a single prominent CTA so the founder doesn't have
 *  to scan the page for the next move. */
function TerminalSummaryBanner({
  brain,
  brandName,
  deferredInFlight,
  onContinue,
}: {
  brain: ScrapeBrainSnapshot['brain'];
  brandName: string;
  deferredInFlight: boolean;
  onContinue: () => void;
}) {
  const productCount = brain?.products?.length ?? 0;
  const teamCount = brain?.team?.length ?? 0;
  const testimonialCount = brain?.testimonials?.length ?? 0;
  const voiceSampleCount = brain?.voiceExamples?.length ?? 0;
  const screenshotCount = brain?.guidelines?.referenceScreenshots?.length ?? 0;
  const hasPalette =
    Boolean(brain?.guidelines?.colors?.primary) || Boolean(brain?.guidelines?.colors?.ink);

  const stats: Array<{ n: number | null; label: string }> = [
    { n: productCount, label: productCount === 1 ? 'product' : 'products' },
    { n: teamCount, label: teamCount === 1 ? 'team member' : 'team' },
    { n: testimonialCount, label: testimonialCount === 1 ? 'quote' : 'quotes' },
    { n: voiceSampleCount, label: voiceSampleCount === 1 ? 'voice sample' : 'voice samples' },
    { n: screenshotCount, label: screenshotCount === 1 ? 'screenshot' : 'screenshots' },
    { n: hasPalette ? null : 0, label: 'palette' },
  ].filter((s) => (s.n === null ? true : s.n > 0));

  return (
    <header className="mag-stagger-in flex flex-col gap-5 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--sage)] sm:p-6">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <div className="shrink-0">
          <Mag size={72} mood="default" trackCursor />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--sage)]">
            {'↘'} <Trans>mag is done reading</Trans>
          </p>
          <h1 className="mt-1.5 font-display text-[26px] sm:text-[32px] font-black leading-[1.04] tracking-[-0.03em] text-[var(--ink)]">
            <Trans>i've got {brandName.toLowerCase()} in my head.</Trans>
          </h1>
          <p className="mt-2 font-serif text-[14.5px] leading-[1.5] text-[var(--ink)]/80">
            <Trans>
              “your products, your voice, your customer, your palette — i pulled what i could read
              off the page. let me walk you through it so we agree on what i got right before i
              start making.”
            </Trans>
          </p>
        </div>
      </div>

      {stats.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {stats.map((s) => (
            <li
              key={s.label}
              className="inline-flex items-baseline gap-1.5 rounded-full border border-[var(--ink)]/15 bg-[var(--cream)] px-2.5 py-1"
            >
              {s.n !== null ? (
                <span className="font-display text-[13px] font-bold leading-none text-[var(--ink)]">
                  {s.n}
                </span>
              ) : (
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--sage)]" />
              )}
              <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/65">
                {s.label}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
        {deferredInFlight ? (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45 sm:mr-auto">
            {'↘'} <Trans>still classifying your images in the background</Trans>
          </p>
        ) : null}
        <button type="button" onClick={onContinue} className="btn self-end sm:self-auto">
          <Trans>walk me through it →</Trans>
        </button>
      </div>
    </header>
  );
}

/** Stall banner — slots in above the step grid when the active step
 *  hasn't moved in STALL_THRESHOLD_MS. Lets the founder either start
 *  the scrape over or sit tight if they think it's still working. */
function StallBanner({
  stalledStep,
  onRestart,
  restarting,
  restartError,
}: {
  stalledStep: string | null;
  onRestart: () => void;
  restarting: boolean;
  restartError: string | null;
}) {
  return (
    <aside className="mt-5 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--pulse)] sm:p-5">
      <div className="flex items-start gap-3">
        <Mag size={48} mood="confused" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
            {'↘'} <Trans>this stalled</Trans>
          </p>
          <h3 className="mt-1 font-display text-[16px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            {stalledStep ? (
              <Trans>i hit a wall on {stalledStep}.</Trans>
            ) : (
              <Trans>i hit a wall mid-read.</Trans>
            )}
          </h3>
          <p className="mt-2 font-serif text-[13px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              “sometimes a fetch hangs, or a model takes the long way around. let me start over —
              usually clears it.”
            </Trans>
          </p>
          {restartError ? (
            <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--pulse)]">
              <Trans>↘ restart failed: {restartError}</Trans>
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onRestart}
          disabled={restarting}
          className="btn btn-sm shrink-0 disabled:opacity-60"
        >
          {restarting ? <Trans>restarting…</Trans> : <Trans>start over →</Trans>}
        </button>
      </div>
    </aside>
  );
}

/** Hard-failure surface for a fetch_html abort — the site was private,
 *  not published, unreachable, or blocking us, so the scrape produced
 *  nothing. Per spec §12.6 the founder always has a way forward: retry
 *  (transient blips), fix the url, or skip the scrape and describe the
 *  business themselves. Right-aligned actions, primary ("try again")
 *  rightmost per the project's button convention. */
function ScrapeErrorBanner({
  brandName,
  detail,
  outOfTokens,
  onTopUp,
  onRetry,
  retrying,
  retryError,
  onChangeUrl,
  onFillManually,
}: {
  brandName: string;
  detail: string | null;
  outOfTokens: boolean;
  onTopUp: () => void;
  onRetry: () => void;
  retrying: boolean;
  retryError: string | null;
  onChangeUrl: () => void;
  onFillManually: () => void;
}) {
  return (
    <aside className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--pulse)] sm:p-6">
      <div className="flex items-start gap-3">
        <Mag size={56} mood="confused" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
            {outOfTokens ? (
              <Trans>↘ i’m out of tokens</Trans>
            ) : (
              <Trans>↘ i couldn't read your site</Trans>
            )}
          </p>
          <h3 className="mt-1 font-display text-[20px] sm:text-[24px] font-black leading-tight tracking-[-0.02em] text-[var(--ink)]">
            {outOfTokens ? (
              <Trans>i ran out of tokens mid-read.</Trans>
            ) : (
              <Trans>{brandName.toLowerCase()} wouldn't open for me.</Trans>
            )}
          </h3>
          <p className="mt-2 font-serif text-[14px] leading-[1.5] text-[var(--ink)]/75">
            {outOfTokens ? (
              <Trans>
                “i need mag tokens to read your site and write your brain. top up and i’ll pick
                right back up — or tell me about it yourself and we’ll keep going.”
              </Trans>
            ) : (
              <Trans>
                “i couldn’t pull the page — it might be private, not published yet, or just blocking
                me. check the url, or tell me about it yourself and we’ll keep going.”
              </Trans>
            )}
          </p>
          {detail ? (
            <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
              ↘ {detail}
            </p>
          ) : null}
          {retryError ? (
            <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--pulse)]">
              <Trans>↘ retry failed: {retryError}</Trans>
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-end gap-2.5">
        <button type="button" onClick={onFillManually} className="btn btn-ghost btn-sm">
          <Trans>i'll fill it in myself</Trans>
        </button>
        {outOfTokens ? (
          // Retry / change-url don't help when the wall is budget, not the
          // site — only a top-up does. Surface that as the one primary.
          <button type="button" onClick={onTopUp} className="btn btn-sm">
            <Trans>top up →</Trans>
          </button>
        ) : (
          <>
            <button type="button" onClick={onChangeUrl} className="btn btn-ghost btn-sm">
              <Trans>use a different url</Trans>
            </button>
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              className="btn btn-sm disabled:opacity-60"
            >
              {retrying ? <Trans>trying again…</Trans> : <Trans>try again →</Trans>}
            </button>
          </>
        )}
      </div>
    </aside>
  );
}

/** [12.2L-173] "↘ now: X · ↗ next: Y" indicator. Sits at the top of
 *  the inspector aside so the founder reads which step is actively
 *  in flight and what's queued up immediately after. Now-row gets a
 *  pulsing plum dot; next-row is a static dim chevron — the visual
 *  hierarchy reinforces "this is happening, this is what's coming."
 *  Pre-existing STEP_LABELS map carries the Mag-voice strings. */
function NowNextTicker({
  activeStep,
  nextStep,
}: {
  activeStep: ExtractionStepRow | null;
  nextStep: ExtractionStepRow | null;
}) {
  if (!activeStep && !nextStep) return null;
  return (
    <div className="mt-0.5 flex flex-col gap-1 rounded-lg border border-[var(--ink)]/10 bg-[var(--paper)]/60 px-2.5 py-2">
      {activeStep ? (
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-[var(--plum)]"
          />
          <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--plum)]">
            <Trans>now</Trans>
          </span>
          <span className="truncate text-[12px] text-[var(--ink)]/85">
            {i18n._(STEP_LABELS[activeStep.step])}
          </span>
        </div>
      ) : null}
      {nextStep ? (
        <div className="flex items-center gap-1.5">
          <span aria-hidden="true" className="font-mono text-[10px] text-[var(--ink)]/35">
            ↗
          </span>
          <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/40">
            <Trans>next</Trans>
          </span>
          <span className="truncate text-[12px] text-[var(--ink)]/55">
            {i18n._(STEP_LABELS[nextStep.step])}
          </span>
        </div>
      ) : null}
    </div>
  );
}
